import { Box, Button, FormControl, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { usePlanningStore } from '@planning/AppProvider'
import { TomAutoComplete, ContainerMobile, useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { Controller, useForm } from 'react-hook-form'
import { CarrierTypeSelection } from './Components/WorkAssignments/CarrierTypeSelection'
import { TallyGangOption } from './TallyclerkHomePage'

interface Props {
  onContinueClick: () => void
  gangs: TallyGangOption[]
  onFetchGangs: () => void
}

export const TallyclerkInitialSelection = observer(
  ({ onContinueClick, gangs, onFetchGangs }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { tallymanSelectVesselAndOperationViewStore: store, tenantStore } = usePlanningStore()
    const { selectedGang } = store
    const skipGangSelection = tenantStore.skipGangSelection

    useAsyncFetch(async () => {
      if (!skipGangSelection) {
        await onFetchGangs()
      }
    }, [onFetchGangs, skipGangSelection])

    const {
      control,
      formState: { errors },
      handleSubmit,
    } = useForm({
      defaultValues: {
        selectedGang: selectedGang || undefined,
      },
    })
    const onSubmit = handleSubmit(data => {
      if (!skipGangSelection && !data?.selectedGang) {
        return
      }
      store.setSelectedGang(skipGangSelection ? undefined : data.selectedGang)
      onContinueClick()
    })

    const skipRailTally = tenantStore.skipRailTally
    const skipVesselTally = tenantStore.skipVesselTally
    store.preselectTrainType(skipRailTally, skipVesselTally)
    return (
      <ContainerMobile
        sx={{
          backgroundColor: theme.palette.grey[200],
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack
          component='form'
          noValidate
          autoComplete='off'
          spacing={theme.customSpacing.l}
          justifyContent='center'
          alignItems='center'
          width={300}
          onSubmit={onSubmit}
        >
          <Typography variant='h1' gutterBottom>
            {t('home', 'Home')}
          </Typography>
          {!skipGangSelection && (
            <Controller
              control={control}
              name='selectedGang'
              rules={{ required: t('fieldIsRequired', 'Field is required') }}
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth required>
                  <TomAutoComplete
                    sx={{ width: '100%' }}
                    options={gangs}
                    required
                    entityKey='id'
                    entityDisplayValue='name'
                    dataCy='select-gang-autocomplete'
                    label={t('selectGang', 'Select gang')}
                    multiple={false}
                    defaultValue={gangs.find(option => option.id === value?.id)}
                    onChangeEntity={entity => {
                      onChange(entity)
                    }}
                    error={!!errors.selectedGang}
                    renderOption={(props, option) => (
                      <Box
                        {...props}
                        key={option.id}
                        component='li'
                        data-cy={`gang-option-${option.id}`}
                      >
                        <Typography variant='body1'>{option.name}</Typography>
                      </Box>
                    )}
                  />
                </FormControl>
              )}
            />
          )}

          {!skipRailTally && !skipVesselTally && (
            <CarrierTypeSelection
              carrierType={store.selectedCarrierType}
              onChange={store.selectCarrierType}
              trainCount={store.railTallyStore.railVisits.length}
              vesselCount={store.vesselTallyStore.vesselVisits.length}
            />
          )}
          <Button
            variant='contained'
            color='primary'
            fullWidth
            type='submit'
            data-cy='continue-button'
          >
            {t('continue', 'Continue')}
          </Button>
        </Stack>
      </ContainerMobile>
    )
  },
)
